import "./palette";
import type { Theme, ThemeOptions } from "@mui/material/styles";
import { alpha, darken, lighten } from "@mui/material/styles";
import {
  DEFAULT_THEME_AND_COLORS,
  type ThemeAndColorsBranding,
} from "../types/branding";

export function buildBrandedThemeOptions(
  themeAndColors: ThemeAndColorsBranding = DEFAULT_THEME_AND_COLORS,
  baseTheme?: Theme
): ThemeOptions {
  const { primaryColor, primaryFontColor, headerBackgroundColor, secondaryFontColor } =
    themeAndColors;

  const primaryMain = primaryColor;
  const primaryLight = lighten(primaryMain, 0.25);
  const primaryDark = darken(primaryMain, 0.15);

  const brandedComponents: ThemeOptions["components"] = {
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          backgroundColor: primaryMain,
          color: primaryFontColor,
          "&:hover": {
            backgroundColor: primaryDark,
          },
        },
        outlinedPrimary: {
          borderColor: primaryMain,
          color: primaryMain,
          "&:hover": {
            borderColor: primaryDark,
            backgroundColor: alpha(primaryMain, 0.04),
          },
        },
        textPrimary: {
          color: primaryMain,
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: primaryMain,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          "&.Mui-selected": {
            color: primaryMain,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: primaryMain,
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          "&.Mui-checked": {
            color: primaryMain,
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          "&.Mui-checked": {
            color: primaryMain,
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          "&.Mui-checked": {
            color: primaryMain,
            "& + .MuiSwitch-track": {
              backgroundColor: alpha(primaryMain, 0.5),
            },
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          "&.Mui-selected": {
            backgroundColor: "var(--brand-primary-selected-background, rgba(41, 94, 241, 0.08)) !important",
            "&.active": {
              backgroundColor: 'var(--brand-primary-selected-background, #e7ecfc) !important', color: 'var(--brand-primary, #295ef1) !important',
            },
          },
        },
      },
    },
    MuiDataGrid: {
      defaultProps: {
        slotProps: {
          baseTextField: {
            variant: "standard",
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          "&.Mui-selected": {
            backgroundColor:
              "var(--brand-primary-selected-background, rgba(41, 94, 241, 0.08)) !important",
            color: "var(--brand-primary, #295ef1)",
            "&:hover": {
              backgroundColor:
                "var(--brand-primary-selected-background, rgba(41, 94, 241, 0.12)) !important",
            },
            "& .MuiSvgIcon-root, & .MuiListItemIcon-root .MuiSvgIcon-root": {
              color: "var(--brand-primary, #295ef1)",
            },
          },
        },
      },
    },
  };

  const baseComponents = baseTheme?.components ?? {};

  return {
    palette: {
      primary: {
        main: primaryMain,
        light: primaryLight,
        dark: primaryDark,
        contrastText: primaryFontColor,
      },
      header: {
        main: headerBackgroundColor,
        contrastText: secondaryFontColor,
      },
    },
    components: {
      ...baseComponents,
      ...brandedComponents,
      MuiDataGrid: {
        ...baseComponents.MuiDataGrid,
        ...brandedComponents.MuiDataGrid,
      },
      MuiButton: {
        ...baseComponents.MuiButton,
        ...brandedComponents.MuiButton,
        styleOverrides: {
          ...baseComponents.MuiButton?.styleOverrides,
          ...brandedComponents.MuiButton?.styleOverrides,
        },
      },
      MuiMenuItem: {
        ...baseComponents.MuiMenuItem,
        ...brandedComponents.MuiMenuItem,
        styleOverrides: {
          ...baseComponents.MuiMenuItem?.styleOverrides,
          ...brandedComponents.MuiMenuItem?.styleOverrides,
        },
      },
    },
  };
}
